// Simulated logic for the AI routing layer + KATLAS governance demo path.
// Pure functions — deterministic given the same inputs.

import { MODEL_CATALOG } from "../data/models";

// ---------- Bloom: model routing ----------

// Score each candidate model for a given request + severity.
// Output: { ranked: [{model, score, breakdown}], selected, reason }
export function bloomRoute({ request, severity, mode = "auto" }) {
  const sensitivity = request.sensitivity; // 1-4
  const urgency = Math.min(4, Math.max(1, request.urgency + (severity >= 3 ? 1 : 0)));

  const ranked = MODEL_CATALOG.map((m) => {
    // Normalise factors to 0-1
    const costFactor = 1 - Math.min(1, m.cost_per_1k / 0.02); // cheaper = higher
    const latencyFactor = 1 - Math.min(1, m.latency_ms / 1500); // faster = higher
    const qualityFactor = m.quality / 100;
    const riskFactor = m.risk_handling / 100;

    // Weights shift with urgency + sensitivity.
    // High sensitivity → weight risk_handling and quality higher; cost matters less.
    // High urgency → weight latency higher.
    const wCost = sensitivity >= 3 ? 0.1 : 0.35 - urgency * 0.04;
    const wLatency = 0.1 + urgency * 0.08;
    const wQuality = 0.25 + (sensitivity >= 2 ? 0.05 : 0);
    const wRisk = 0.15 + sensitivity * 0.08;

    const total = wCost + wLatency + wQuality + wRisk;
    const score =
      (costFactor * wCost +
        latencyFactor * wLatency +
        qualityFactor * wQuality +
        riskFactor * wRisk) /
      total;

    return {
      model: m,
      score: Number((score * 100).toFixed(1)),
      breakdown: {
        cost: Number((costFactor * 100).toFixed(0)),
        latency: Number((latencyFactor * 100).toFixed(0)),
        quality: m.quality,
        risk: m.risk_handling,
        weights: {
          cost: Number(wCost.toFixed(2)),
          latency: Number(wLatency.toFixed(2)),
          quality: Number(wQuality.toFixed(2)),
          risk: Number(wRisk.toFixed(2)),
        },
      },
    };
  }).sort((a, b) => b.score - a.score);

  // Override selection by compare mode.
  let selected;
  let reason;
  if (mode === "cheap") {
    selected = ranked.find((r) => r.model.id === "fast-mini");
    reason = "Compare-mode override: cheapest path selected to expose cost trade-off.";
  } else if (mode === "premium") {
    selected = ranked.find((r) => r.model.id === "premium-reasoning");
    reason = "Compare-mode override: top-quality frontier model selected, ignoring cost.";
  } else if (mode === "governed") {
    selected = ranked.find((r) => r.model.id === "governed-safer");
    reason = "Compare-mode override: risk-aware route forced — note the cost premium over Cheap.";
  } else {
    selected = ranked[0];
    reason = buildBloomReason(request, severity, selected);
  }

  return { ranked, selected, reason, urgencyEffective: urgency };
}

function buildBloomReason(request, severity, top) {
  const parts = [];
  if (request.sensitivity >= 3) {
    parts.push("High sensitivity — risk-aware routing prioritised over price.");
  } else if (request.urgency >= 3 || severity >= 3) {
    parts.push("Time-critical — latency weight increased.");
  } else {
    parts.push("Routine request — cost-efficient path acceptable.");
  }
  parts.push(
    `Selected ${top.model.label} (${top.model.family}) at ~$${top.model.cost_per_1k.toFixed(
      4
    )}/1k tokens, ~${top.model.latency_ms}ms.`
  );
  return parts.join(" ");
}

// ---------- KATLAS: governance ----------

// Returns { authority, approval, disclosure, status, decisionId, escalation, rationale }
// status ∈ "advisory-allowed" | "approved" | "held" | "denied" | "escalated"
export function katlasGovern({ request, severity }) {
  const sensitivity = request.sensitivity;
  const isExternal =
    request.proposedAction.toLowerCase().includes("external partner") ||
    request.proposedAction.toLowerCase().includes("disclosure");
  const isPublic = request.proposedAction.toLowerCase().includes("public");

  // Authority check
  let authority;
  if (sensitivity >= 4) {
    authority = { state: "blocked", note: "Action exceeds standing authority — bounded scope." };
  } else if (sensitivity >= 3 || (isExternal && sensitivity >= 2)) {
    authority = { state: "conditional", note: "Within authority subject to review threshold." };
  } else {
    authority = { state: "pass", note: "Within standing authority for role." };
  }

  // Approval requirement
  let approval;
  if (sensitivity >= 4) {
    approval = {
      level: "multi-party",
      reviewers: ["Duty Manager", "Information Governance Lead", "Partner Liaison"],
    };
  } else if (sensitivity >= 3 || (isExternal && sensitivity >= 2)) {
    approval = { level: "single", reviewers: ["Duty Manager"] };
  } else if (isPublic) {
    approval = { level: "advisory-acknowledge", reviewers: ["Communications Lead"] };
  } else {
    approval = { level: "none", reviewers: [] };
  }

  // Disclosure / handoff policy
  let disclosure;
  if (isExternal && sensitivity >= 4) {
    disclosure = {
      policy: "Selective disclosure — fields redacted; partner consent required.",
      allowed: false,
    };
  } else if (isExternal) {
    disclosure = {
      policy: "Selective disclosure — minimum-necessary fields, named partner only.",
      allowed: true,
    };
  } else if (isPublic) {
    disclosure = { policy: "Public channel — review for accuracy, no PII.", allowed: true };
  } else {
    disclosure = { policy: "Internal use only — no external onward share.", allowed: true };
  }

  // Status
  let status;
  let escalation = null;
  if (authority.state === "blocked") {
    status = "held";
    escalation = "Routed to multi-party review. Action paused pending sign-off.";
  } else if (severity >= 4 && sensitivity >= 3) {
    status = "escalated";
    escalation = "Severity-driven escalation: cross-agency notification required.";
  } else if (authority.state === "conditional") {
    status = "approved";
  } else if (approval.level === "advisory-acknowledge") {
    status = "advisory-allowed";
  } else {
    status = "approved";
  }

  // Decision id
  const decisionId = makeDecisionId(request.id, severity);

  // Rationale
  const rationale = [
    `Role check: ${request.ownerRole} → action class "${shorten(request.proposedAction)}".`,
    `Sensitivity ${sensitivity}/4 · Severity ${severity}/4.`,
    authority.note,
    approval.level === "none"
      ? "No human review required for advisory class."
      : `Review path: ${approval.level} (${approval.reviewers.join(", ")}).`,
    disclosure.policy,
  ];

  return {
    authority,
    approval,
    disclosure,
    status,
    escalation,
    decisionId,
    rationale,
    issuedAt: new Date().toISOString(),
  };
}

function shorten(s) {
  return s.length > 38 ? s.slice(0, 36) + "…" : s;
}

function makeDecisionId(reqId, severity) {
  // Deterministic-ish without ledger framing — looks artefactual.
  const stamp = Date.now().toString(36).slice(-5).toUpperCase();
  return `KAT-${reqId.toUpperCase()}-${severity}-${stamp}`;
}

// ---------- Outcome ----------

export function buildOutcome({ request, bloom, katlas }) {
  const partiesNotified = derivePartiesNotified(request, katlas);
  const handoff = deriveHandoff(request, katlas);
  const action = deriveActionTaken(request, katlas);

  return {
    action,
    partiesNotified,
    handoff,
    receipt: {
      id: katlas.decisionId,
      issuedAt: katlas.issuedAt,
      requestTitle: request.title,
      requestingRole: request.ownerRole,
      proposedAction: request.proposedAction,
      modelRoute: `${bloom.selected.model.label} (${bloom.selected.model.family})`,
      cost: bloom.selected.model.cost_per_1k,
      latency_ms: bloom.selected.model.latency_ms,
      authorityState: katlas.authority.state,
      approvalLevel: katlas.approval.level,
      reviewers: katlas.approval.reviewers,
      disclosurePolicy: katlas.disclosure.policy,
      status: katlas.status,
      escalation: katlas.escalation,
    },
    auditTrail: [
      { ts: katlas.issuedAt, label: "Request received", detail: request.title },
      {
        ts: katlas.issuedAt,
        label: "Routing layer selected route",
        detail: `${bloom.selected.model.label} · score ${bloom.selected.score}`,
      },
      {
        ts: katlas.issuedAt,
        label: "KATLAS authority check",
        detail: `${katlas.authority.state.toUpperCase()} — ${katlas.authority.note}`,
      },
      {
        ts: katlas.issuedAt,
        label: "Decision issued",
        detail: `${katlas.decisionId} · status ${katlas.status}`,
      },
    ],
  };
}

function derivePartiesNotified(request, katlas) {
  const parties = new Set([request.ownerRole]);
  katlas.approval.reviewers.forEach((r) => parties.add(r));
  if (request.proposedAction.toLowerCase().includes("public")) parties.add("Public channel");
  if (request.proposedAction.toLowerCase().includes("external partner")) parties.add("External Partner / Agency");
  return Array.from(parties);
}

function deriveHandoff(request, katlas) {
  if (katlas.status === "held" || katlas.status === "denied") {
    return { occurred: false, to: null, note: "No operational handoff — action paused/denied." };
  }
  if (request.proposedAction.toLowerCase().includes("external partner")) {
    return {
      occurred: true,
      to: "External Partner / Agency",
      note: "Selective disclosure handoff with named partner only.",
    };
  }
  if (request.proposedAction.toLowerCase().includes("care providers")) {
    return { occurred: true, to: "Local Service Coordinator → Care Providers", note: "Operational handoff to delivery partners." };
  }
  if (request.proposedAction.toLowerCase().includes("public")) {
    return { occurred: true, to: "Communications Lead", note: "Publication handoff with comms acknowledgement." };
  }
  return { occurred: true, to: "Operations Lead", note: "Internal operational handoff." };
}

function deriveActionTaken(request, katlas) {
  switch (katlas.status) {
    case "held":
      return `Action HELD — "${request.proposedAction}" paused; human review required before any operational step.`;
    case "denied":
      return `Action DENIED — "${request.proposedAction}" not authorised under current policy.`;
    case "escalated":
      return `Escalation REQUIRED — "${request.proposedAction}" proceeding under multi-party oversight.`;
    case "advisory-allowed":
      return `Advisory action APPROVED under policy — "${request.proposedAction}" issued with comms acknowledgement.`;
    case "approved":
    default:
      return `Action APPROVED under policy — "${request.proposedAction}" executed.`;
  }
}
